import { Workout } from '@strength/domain-workout';
import { PrimitiveAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Slot } from '../../../components/sheet';
import { CustomSlot } from '../../../components/sheet/slots';
import { CellInputWithAtom } from '../../../components/sheet/slots/common';
import { WORKOUT_NAME_SLOT } from '../../../components/sheet/slots/slot-labels';
import { cn } from '../../../components/utils';
import { jotaiStore } from '../../../utils/jotai';
import { BlockSelectionData } from '../types';

type WorkoutNameSlotProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  value: string | null;
  inputAtom: PrimitiveAtom<string>;
  workout: Workout;
  onValueChange?: (value: string | null) => void;
  error?: boolean;
  disabled?: boolean;
  gridOptions?: {
    style?: React.CSSProperties;
    overrideGridStyles?: boolean;
  };
};

export function workoutNameSlot({
  x,
  y,
  width,
  height,
  value,
  workout,
  onValueChange,
  inputAtom,
  error,
  disabled,
  gridOptions,
}: WorkoutNameSlotProps): Slot<BlockSelectionData> {
  return new CustomSlot(
    { x, y },
    width,
    height,
    disabled ?? false,
    (editing, onBlur) => (
      <WorkoutNameCellContent
        editing={editing}
        inputAtom={inputAtom}
        value={value}
        onBlur={onBlur}
        onValueChange={onValueChange}
      />
    ),
    {
      backgroundClassName: error ? 'bg-red-100' : undefined,
      selectedBorderStyle: disabled ? 'border-2 border-blue-500/50' : undefined,
      style: gridOptions?.style,
      overrideGridStyles: gridOptions?.overrideGridStyles,
      data: [
        {
          type: 'WorkoutName',
          workoutId: workout.workoutId,
          workout: workout,
          disabled: disabled ?? false,
        },
      ],
      editBehavior: disabled
        ? {
            type: 'skip',
          }
        : {
            type: 'input',
            onStart: (input) => {
              if (input === undefined) {
                jotaiStore.set(inputAtom, value || '');
              } else {
                jotaiStore.set(inputAtom, input);
              }
            },
            onCommit: () => {
              const input = jotaiStore.get(inputAtom);
              onValueChange?.(input || null);
              jotaiStore.set(inputAtom, '');
            },
          },
    },
    WORKOUT_NAME_SLOT,
  );
}

type WorkoutNameCellContentProps = {
  editing: boolean;
  inputAtom: PrimitiveAtom<string>;
  value: string | null;
  onBlur: (committed: boolean) => void;
  onValueChange?: (value: string | null) => void;
};

function WorkoutNameCellContent({
  editing,
  inputAtom,
  value,
  onBlur,
  onValueChange,
}: WorkoutNameCellContentProps) {
  const { t } = useTranslation();
  if (editing) {
    return (
      <CellInputWithAtom
        inputAtom={inputAtom}
        onBlur={() => {
          const input = jotaiStore.get(inputAtom);
          onValueChange?.(input || null);
          jotaiStore.set(inputAtom, '');
          onBlur(true);
        }}
      />
    );
  }

  return (
    <span
      className={cn('flex px-2.5 py-2', value ? 'text-black' : 'text-gray-500')}
    >
      {value || t('workoutNamePlaceholder')}
    </span>
  );
}
